(() => {
  if (window.__illegalNightCollectionLoaded) return;
  window.__illegalNightCollectionLoaded = true;

  const headerSelectors = [
    '.shopify-section-group-header-group',
    '.shopify-section-group-header-group .shopify-section',
    'sticky-header',
    '.header-wrapper.header-fixed',
    '.header-wrapper.header-sticky',
  ].join(',');

  const stickySubscribers = new Set();
  let stickyFrame = null;
  let headerResizeObserver = null;
  let headerMutationObserver = null;
  let stickyControllerStarted = false;

  const getHeaderElements = () => Array.from(document.querySelectorAll(headerSelectors));

  const getHeaderOffset = () => {
    let visibleBottom = 0;

    getHeaderElements().forEach((element) => {
      const styles = window.getComputedStyle(element);
      if (!['fixed', 'sticky'].includes(styles.position)) return;
      if (styles.display === 'none' || styles.visibility === 'hidden') return;

      const rect = element.getBoundingClientRect();
      const isVisible = rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      if (!isVisible) return;

      visibleBottom = Math.max(visibleBottom, Math.min(rect.bottom, window.innerHeight));
    });

    return Math.max(0, Math.round(visibleBottom));
  };

  const notifyStickySubscribers = () => {
    stickyFrame = null;
    const headerOffset = getHeaderOffset();
    stickySubscribers.forEach((callback) => callback(headerOffset));
  };

  const scheduleStickyUpdate = () => {
    if (stickyFrame) return;
    stickyFrame = window.requestAnimationFrame(notifyStickySubscribers);
  };

  const observeHeaderSizes = () => {
    if (!headerResizeObserver) return;
    headerResizeObserver.disconnect();
    getHeaderElements().forEach((element) => headerResizeObserver.observe(element));
  };

  const startStickyController = () => {
    if (stickyControllerStarted) return;
    stickyControllerStarted = true;

    window.addEventListener('resize', scheduleStickyUpdate, { passive: true });
    window.addEventListener('scroll', scheduleStickyUpdate, { passive: true });

    if ('ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(scheduleStickyUpdate);
      observeHeaderSizes();
    }

    const headerRoot =
      document.querySelector('.shopify-section-group-header-group') ||
      document.querySelector('.header-wrapper')?.parentElement;

    if (headerRoot && 'MutationObserver' in window) {
      headerMutationObserver = new MutationObserver(() => {
        observeHeaderSizes();
        scheduleStickyUpdate();
      });
      headerMutationObserver.observe(headerRoot, {
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    scheduleStickyUpdate();
  };

  const subscribeToStickyUpdates = (callback) => {
    stickySubscribers.add(callback);
    startStickyController();
    scheduleStickyUpdate();
    return () => stickySubscribers.delete(callback);
  };

  class IllegalNightCollectionNav extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.updatePosition = this.updatePosition.bind(this);
      this.unsubscribeSticky = subscribeToStickyUpdates(this.updatePosition);

      const active = this.querySelector('[aria-current="page"]');
      if (active) {
        requestAnimationFrame(() => active.scrollIntoView({ block: 'nearest', inline: 'center' }));
      }
    }

    disconnectedCallback() {
      this.unsubscribeSticky?.();
    }

    updatePosition(headerOffset = getHeaderOffset()) {
      this.style.setProperty('--in-sticky-top', `${headerOffset}px`);
    }
  }

  class IllegalNightCollectionGrid extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      this.onClick = this.onClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDetailsToggle = this.onDetailsToggle.bind(this);
      this.onFilterButtonClick = this.onFilterButtonClick.bind(this);
      this.onSortToggle = this.onSortToggle.bind(this);
      this.updatePosition = this.updatePosition.bind(this);
      this.prepareNativeFilterDrawer = this.prepareNativeFilterDrawer.bind(this);
      this.syncActiveFacets = this.syncActiveFacets.bind(this);
      this.syncSortState = this.syncSortState.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('click', this.onDocumentClick);

      this.filterButton = this.querySelector('[data-in-open-filters]');
      this.filterButton?.addEventListener('click', this.onFilterButtonClick);

      this.mobileDetails = this.querySelector('.mobile-facets__disclosure');
      this.mobileDetails?.addEventListener('toggle', this.onDetailsToggle);

      this.sortDetails = this.querySelector('[data-in-sort]');
      this.sortDetails?.addEventListener('toggle', this.onSortToggle);

      this.nativeFacets = this.querySelector('.in-native-facets');
      if (this.nativeFacets && 'MutationObserver' in window) {
        this.facetsObserver = new MutationObserver(() => {
          this.prepareNativeFilterDrawer();
          this.syncActiveFacets();
          this.syncSortState();
        });
        this.facetsObserver.observe(this.nativeFacets, { childList: true, subtree: true });
      }

      this.prepareNativeFilterDrawer();
      this.syncActiveFacets();
      this.syncSortState();
      this.unsubscribeSticky = subscribeToStickyUpdates(this.updatePosition);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('click', this.onDocumentClick);
      this.filterButton?.removeEventListener('click', this.onFilterButtonClick);
      this.mobileDetails?.removeEventListener('toggle', this.onDetailsToggle);
      this.sortDetails?.removeEventListener('toggle', this.onSortToggle);
      this.facetsObserver?.disconnect();
      this.unsubscribeSticky?.();
      this.classList.remove('in-filters-open');
      this.querySelector('.in-toolbar-wrap')?.classList.remove('is-pinned');
    }

    updatePosition(headerOffset = getHeaderOffset()) {
      let toolbarTop = headerOffset;
      const gridDocumentTop = this.getBoundingClientRect().top + window.scrollY;
      const toolbarWrap = this.querySelector('.in-toolbar-wrap');

      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const drawerTop = isMobile ? Math.max(0, headerOffset - 1) : headerOffset;
      this.style.setProperty('--in-header-offset', `${drawerTop}px`);

      if (isMobile) {
        this.style.removeProperty('--in-toolbar-top');
        toolbarWrap?.classList.remove('is-pinned');
        return;
      }

      document
        .querySelectorAll('illegal-night-collection-nav[data-sticky="true"]')
        .forEach((nav) => {
          const navRect = nav.getBoundingClientRect();
          const navDocumentTop = navRect.top + window.scrollY;
          const isBeforeGrid = navDocumentTop < gridDocumentTop;
          const isPinned = navRect.top <= headerOffset + 1 && navRect.bottom > headerOffset;

          if (isBeforeGrid && isPinned) {
            toolbarTop = Math.max(toolbarTop, Math.round(navRect.bottom));
          }
        });

      // Overlap the adjacent sticky surface by 2px to remove the subpixel/border seam.
      const resolvedToolbarTop = Math.max(0, toolbarTop - 2);
      this.style.setProperty('--in-toolbar-top', `${resolvedToolbarTop}px`);

      if (toolbarWrap) {
        const toolbarRect = toolbarWrap.getBoundingClientRect();
        const canStick = toolbarWrap.classList.contains('is-sticky');
        const isPinned = canStick && Math.abs(toolbarRect.top - resolvedToolbarTop) <= 2;
        toolbarWrap.classList.toggle('is-pinned', isPinned);
      }
    }

    syncActiveFacets() {
      const container = this.querySelector('.active-facets-container');
      const row = container?.closest('.collection-filters');
      const activeButtons = container
        ? container.querySelectorAll(
            '.active-facets__button:not(.active-facets__button--clear)'
          )
        : [];
      const activeCount = activeButtons.length;

      row?.classList.toggle('in-active-facets--empty', activeCount === 0);
      const nativeFacets = this.querySelector('.in-native-facets');
      if (nativeFacets) nativeFacets.dataset.activeCount = String(activeCount);

      const countBadge = this.querySelector('[data-in-filter-count]');
      if (countBadge) {
        countBadge.textContent = String(activeCount);
        countBadge.classList.toggle('in-toolbar__count--empty', activeCount === 0);
      }

      const filterButton = this.querySelector('[data-in-open-filters]');
      if (filterButton) {
        filterButton.setAttribute(
          'aria-label',
          activeCount > 0 ? `Otwórz filtry. Aktywne filtry: ${activeCount}` : 'Otwórz filtry'
        );
      }
    }

    prepareNativeFilterDrawer() {
      const filterLabel =
        this.querySelector('[data-in-open-filters] .in-toolbar__text > span')?.textContent.trim() ||
        'Filtry';

      this.querySelectorAll('.mobile-facets__heading').forEach((heading) => {
        if (heading.textContent.trim() !== filterLabel) heading.textContent = filterLabel;
      });
    }

    onDetailsToggle() {
      const expanded = Boolean(this.mobileDetails?.open);
      const filterButton = this.querySelector('[data-in-open-filters]');
      filterButton?.setAttribute('aria-expanded', String(expanded));
      this.classList.toggle('in-filters-open', expanded);

      if (!expanded && this.lastFilterTrigger?.isConnected) {
        requestAnimationFrame(() => this.lastFilterTrigger.focus());
      }
    }

    onFilterButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this.openNativeFilters(event.currentTarget);
    }

    openNativeFilters(trigger) {
      const details = this.querySelector('.mobile-facets__disclosure');
      const summary = details?.querySelector(':scope > summary');
      const drawer = details?.closest('menu-drawer');
      if (!details || !summary) return;

      if (this.mobileDetails !== details) {
        this.mobileDetails?.removeEventListener('toggle', this.onDetailsToggle);
        this.mobileDetails = details;
        this.mobileDetails.addEventListener('toggle', this.onDetailsToggle);
      }

      this.closeSort(false);
      this.lastFilterTrigger = trigger;

      details.open = true;
      details.classList.add('menu-opening');
      summary.setAttribute('aria-expanded', 'true');
      this.classList.add('in-filters-open');
      document.body.classList.add('overflow-hidden');

      if (typeof drawer?.openMenuDrawer === 'function') {
        drawer.openMenuDrawer(summary);
      }

      window.setTimeout(() => {
        const target =
          details.querySelector('.mobile-facets__close') ||
          details.querySelector('.mobile-facets__summary');
        target?.focus({ preventScroll: true });
      }, 180);
    }

    onSortToggle() {
      if (!this.sortDetails) return;
      const expanded = this.sortDetails.open;
      const toggle = this.sortDetails.querySelector('[data-in-sort-toggle]');
      toggle?.setAttribute('aria-expanded', String(expanded));

      if (expanded) {
        const activeOption =
          this.sortDetails.querySelector('[data-in-sort-option][aria-selected="true"]') ||
          this.sortDetails.querySelector('[data-in-sort-option]');
        requestAnimationFrame(() => activeOption?.focus());
      }
    }

    onDocumentClick(event) {
      if (!this.sortDetails?.open || this.sortDetails.contains(event.target)) return;
      this.closeSort(false);
    }

    onKeyDown(event) {
      if (!this.sortDetails?.open) return;

      const currentOption = event.target.closest?.('[data-in-sort-option]');
      const navigationKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];

      if (currentOption && navigationKeys.includes(event.key)) {
        const options = Array.from(this.sortDetails.querySelectorAll('[data-in-sort-option]'));
        const currentIndex = options.indexOf(currentOption);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
        if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = options.length - 1;

        event.preventDefault();
        options[nextIndex]?.focus();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeSort(true);
      }
    }

    closeSort(returnFocus) {
      if (!this.sortDetails?.open) return;
      this.sortDetails.open = false;
      if (returnFocus) {
        const toggle = this.sortDetails.querySelector('[data-in-sort-toggle]');
        requestAnimationFrame(() => toggle?.focus());
      }
    }

    getNativeSortSelect() {
      return (
        this.querySelector('#SortBy-outside-drawer') ||
        this.querySelector('#SortBy-mobile')
      );
    }

    syncSortState(preferredValue) {
      const nativeSelect = this.getNativeSortSelect();
      const value =
        preferredValue ||
        nativeSelect?.value ||
        this.dataset.inCurrentSort ||
        '';

      if (!value) return;
      this.dataset.inCurrentSort = value;

      let activeLabel = '';
      this.querySelectorAll('[data-in-sort-option]').forEach((option) => {
        const active = option.dataset.inSortValue === value;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', String(active));
        if (active) activeLabel = option.textContent.trim();
      });

      const toggle = this.querySelector('[data-in-sort-toggle]');
      if (toggle && activeLabel) {
        toggle.setAttribute('aria-label', `Otwórz sortowanie. Wybrano: ${activeLabel}`);
      }
    }

    selectSort(value) {
      const nativeSelect = this.getNativeSortSelect();
      if (!nativeSelect || !value) return;

      nativeSelect.value = value;
      this.syncSortState(value);
      this.closeSort(true);
      nativeSelect.dispatchEvent(new Event('input', { bubbles: true }));
    }

    setGrid(kind, columns) {
      const validValues = kind === 'desktop' ? [3, 4] : [1, 2];
      if (!validValues.includes(columns)) return;

      validValues.forEach((value) => {
        this.classList.toggle(`in-grid-${kind}-${value}`, value === columns);
      });

      this.querySelectorAll(`[data-in-grid-${kind}]`).forEach((button) => {
        const active = Number.parseInt(button.dataset[`inGrid${kind[0].toUpperCase()}${kind.slice(1)}`], 10) === columns;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    onClick(event) {
      const filterButton = event.target.closest('[data-in-open-filters]');
      const sortOption = event.target.closest('[data-in-sort-option]');
      const desktopGridButton = event.target.closest('[data-in-grid-desktop]');
      const mobileGridButton = event.target.closest('[data-in-grid-mobile]');

      if (filterButton) {
        this.openNativeFilters(filterButton);
        return;
      }

      if (sortOption) {
        this.selectSort(sortOption.dataset.inSortValue);
        return;
      }

      if (desktopGridButton) {
        this.setGrid('desktop', Number.parseInt(desktopGridButton.dataset.inGridDesktop, 10));
        return;
      }

      if (mobileGridButton) {
        this.setGrid('mobile', Number.parseInt(mobileGridButton.dataset.inGridMobile, 10));
      }
    }
  }

  class IllegalNightSeo extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      this.button = this.querySelector('.in-seo__toggle');
      this.content = this.button
        ? document.getElementById(this.button.getAttribute('aria-controls'))
        : null;
      this.onToggle = this.onToggle.bind(this);
      this.button?.addEventListener('click', this.onToggle);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onToggle);
    }

    onToggle() {
      if (!this.button || !this.content) return;
      const expanded = this.button.getAttribute('aria-expanded') === 'true';
      this.button.setAttribute('aria-expanded', String(!expanded));
      this.content.hidden = expanded;
      const label = expanded ? this.button.dataset.labelMore : this.button.dataset.labelLess;
      const labelNode = this.button.querySelector('span');
      if (labelNode) labelNode.textContent = label;
    }
  }

  if (!customElements.get('illegal-night-collection-nav')) {
    customElements.define('illegal-night-collection-nav', IllegalNightCollectionNav);
  }
  if (!customElements.get('illegal-night-collection-grid')) {
    customElements.define('illegal-night-collection-grid', IllegalNightCollectionGrid);
  }
  if (!customElements.get('illegal-night-seo')) {
    customElements.define('illegal-night-seo', IllegalNightSeo);
  }

  document.addEventListener('shopify:section:load', () => {
    observeHeaderSizes();
    scheduleStickyUpdate();
    document
      .querySelectorAll('illegal-night-collection-grid')
      .forEach((element) => element.syncActiveFacets?.());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    event.target?.scrollIntoView?.({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
      inline: 'center',
    });
  });
})();
